<script setup lang="ts">
import {ref, computed, watch} from 'vue'
import {useField} from 'vee-validate'
import api from "../api.ts";

const props = defineProps<{
  name: string
  currentEmail?: string
}>()

const model = defineModel<string>()

const email = useField<string>(props.name)
const emailAvailable = ref<boolean | null>(null)
const checkingEmail = ref(false)
const emailCheckTimeout = ref<ReturnType<typeof setTimeout>>()

const getEmailMessages = computed(() => {
  if (email.errorMessage.value) {
    return [email.errorMessage.value]
  }

  if (emailAvailable.value === false) {
    return ['This email is already taken.']
  }

  if (emailAvailable.value === true) {
    return ['This email is available!']
  }

  return []
})

// Sync email.value and model
watch(() => email.value.value, (val) => {
  if (val !== model.value) {
    model.value = val
  }
})
watch(() => model.value, (val) => {
  if (val !== email.value.value) {
    email.value.value = val
  }
})

watch(() => email.value.value, (newValue) => {
  model.value = newValue
  emailAvailable.value = null

  if (emailCheckTimeout.value) {
    clearTimeout(emailCheckTimeout.value)
  }

  if (!newValue || newValue.length < 3) return

  if(props.currentEmail && newValue.toLowerCase() === props.currentEmail.toLowerCase()) {
    emailAvailable.value = true
    return
  }

  emailCheckTimeout.value = setTimeout(async () => {
    checkingEmail.value = true

    try {
      const res = await api.get('/api/v4/users/email-available', {
        params: { email: newValue }
      })
      emailAvailable.value = res.data.available
    } catch (error) {
      console.error('email check failed', error)
      emailAvailable.value = null
    } finally {
      checkingEmail.value = false
    }
  }, 500)
})

defineExpose({
  errorMessage: email.errorMessage,
  value: email.value,
  emailAvailable,
})
</script>

<template>
  <v-text-field
      class="my-2"
      label="Email*"
      placeholder="you@example.com"
      v-model="email.value.value"
      :append-inner-icon="
      checkingEmail
        ? 'mdi-loading'
        : emailAvailable === true
          ? 'mdi-check-circle'
          : emailAvailable === false
            ? 'mdi-close-circle'
            : ''
    "
      :error="!!email.errorMessage.value || emailAvailable === false"
      :success="!email.errorMessage.value && emailAvailable === true"
      :color="emailAvailable === true && !email.errorMessage.value ? 'success' : undefined"
      :messages="getEmailMessages"
  />
</template>
